import React from "react";
import moment from "moment-timezone";
import { useRouteMatch } from "react-router-dom";
import { SelectChangeEvent, Box } from "@mui/material";

import { ITradedAccount } from "../../../../../../api/funds/index.types";
import { checkIsCompletedAccount } from "../../../../utils";
import {
  HOURS_12_FORMAT_WITH_DATE,
  INVOICE_LIVE_SIM_STATUSES_OPTIONS,
  INVOICE_LIVE_STATUSES_OPTIONS,
  PROCESSING_STATUSES_OPTIONS,
  EVALUATION_TYPE_OPTIONS,
  DEPRECATED_INVOICE_STATUSES_OPTIONS,
} from "../../../../../../constants";
import AccountsProcessingAndInvoiceStatusSelect from "../../../AccountsProcessingAndInvoiceStatusSelect";

interface IProps {
  accountsToShow: ITradedAccount[];
  listItems: ITradedAccount[];
  priorAccounts: ITradedAccount[];

  handleChangeValue: (
    e: SelectChangeEvent<string>,
    id: number,
    accounts_id: number,
    e2t_account_id: string
  ) => void;
}
const AccountsList: React.FC<IProps> = ({
  accountsToShow,
  listItems,
  priorAccounts,
  handleChangeValue,
}) => {
  const isMatchFundedComplianceProfilePage = useRouteMatch("/fundedCompliance/:id");

  return (
    <Box
      sx={{
        ml: "15px",
        mt: "5px",
        display: "grid",
        gridTemplateColumns: "auto auto auto auto auto auto auto auto auto auto",
        gridGap: "1px",
      }}
    >
      <Box>
        <b>Account creation time</b>
      </Box>
      <Box>
        <b>Account name</b>
      </Box>
      <Box>
        <b>Prop firm</b>
      </Box>
      <Box>
        <b>Type</b>
      </Box>
      <Box>
        <b>Account size</b>
      </Box>
      <Box>
        <b>Initial TCP account</b>
      </Box>
      <Box>
        <b>E2T Account name</b>
      </Box>
      <Box>
        <b>Evaluation type</b>
      </Box>
      <Box>
        <b>Processing status</b>
      </Box>
      <Box>
        <b>Invoice status</b>
      </Box>

      {accountsToShow.map((a, index) => {
        const {
          id,
          failed_timestamp,
          timestamp,
          e2t_account_id,
          prop_firm,
          account_type,
          processing_status,
          accounts_id,
          invoice_status,
          original_account,
          initial_tcp_account_id,
          evaluation_completed_history,
          evaluation_type,
        } = a;
        const prevAccount = accountsToShow[index - 1];
        const isSubAccount =
          prevAccount &&
          initial_tcp_account_id &&
          (initial_tcp_account_id === prevAccount.id ||
            initial_tcp_account_id === prevAccount.initial_tcp_account_id);

        const initialTCPAccount = [...priorAccounts, ...listItems].find(
          ({ id }) => id === initial_tcp_account_id
        ) || { e2t_account_id: null };

        const time = moment(failed_timestamp || timestamp)
          .tz("America/Chicago")
          .format(HOURS_12_FORMAT_WITH_DATE);

        /**
         * Allow the compliance team to edit the latest
         * or the first traded account (if there is only one)
         */
        const isAllowedToEditByCompliance =
          isMatchFundedComplianceProfilePage && index !== accountsToShow.length - 1;
        const isCompletedAccount = checkIsCompletedAccount(
          processing_status,
          original_account,
          a,
          accountsToShow
        );
        const isDisabledToEdit = isCompletedAccount || isAllowedToEditByCompliance;

        return (
          <React.Fragment key={id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ...(isSubAccount && { pl: "20px" }),
              }}
            >
              {time}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>{e2t_account_id}</Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>{prop_firm}</Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>{account_type}</Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>{original_account}</Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>{initialTCPAccount.e2t_account_id}</Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>{evaluation_completed_history?.e2t_account_id}</Box>
            <Box>
              {isDisabledToEdit ? (
                evaluation_type
              ) : (
                <AccountsProcessingAndInvoiceStatusSelect
                  options={EVALUATION_TYPE_OPTIONS}
                  name="evaluation_type"
                  label=""
                  value={evaluation_type || ""}
                  id={id}
                  accounts_id={accounts_id}
                  handleChange={(e, id, accounts_id) => {
                    handleChangeValue(e, id, accounts_id, e2t_account_id);
                  }}
                />
              )}
            </Box>
            <Box>
              {isDisabledToEdit ? (
                processing_status
              ) : (
                <AccountsProcessingAndInvoiceStatusSelect
                  options={PROCESSING_STATUSES_OPTIONS}
                  name="processing_status"
                  label=""
                  value={processing_status || ""}
                  id={id}
                  accounts_id={accounts_id}
                  handleChange={(e, id, accounts_id) => {
                    handleChangeValue(e, id, accounts_id, e2t_account_id);
                  }}
                />
              )}
            </Box>
            <Box>
              {isDisabledToEdit ? (
                invoice_status
              ) : (
                <AccountsProcessingAndInvoiceStatusSelect
                  options={
                    account_type === "LiveSim"
                      ? [
                          ...INVOICE_LIVE_SIM_STATUSES_OPTIONS,
                          ...DEPRECATED_INVOICE_STATUSES_OPTIONS,
                        ]
                      : [...INVOICE_LIVE_STATUSES_OPTIONS, ...DEPRECATED_INVOICE_STATUSES_OPTIONS]
                  }
                  name="invoice_status"
                  label=""
                  value={invoice_status || ""}
                  id={id}
                  accounts_id={accounts_id}
                  handleChange={(e, id, accounts_id) => {
                    handleChangeValue(e, id, accounts_id, e2t_account_id);
                  }}
                />
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default AccountsList;
